import axios from 'axios';
import { getBackendUrl } from './getBackendUrl';

const API = `${getBackendUrl()}/api`;

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function getPushState() {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'subscribed' : 'not_subscribed';
  } catch {
    return 'not_subscribed';
  }
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error('Push notifications are not supported on this device/browser');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted');
  const { data } = await axios.get(`${API}/push/public-key`);
  if (!data.publicKey) throw new Error('Push is not configured on the server');
  const serverKey = urlBase64ToUint8Array(data.publicKey);
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // Self-heal: if the existing subscription was made with a DIFFERENT server key, replace it
  if (sub && sub.options?.applicationServerKey) {
    const existingKey = new Uint8Array(sub.options.applicationServerKey);
    const same = existingKey.length === serverKey.length && existingKey.every((b, i) => b === serverKey[i]);
    if (!same) {
      try { await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }); } catch { /* best effort */ }
      await sub.unsubscribe();
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: serverKey,
    });
  }
  await axios.post(`${API}/push/subscribe`, sub.toJSON());
  return sub;
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint });
  } catch { /* server cleanup best-effort */ }
  await sub.unsubscribe();
}

export async function sendTestPush() {
  await axios.post(`${API}/push/test`);
}

export async function updateAppBadge() {
  try {
    const { data } = await axios.get(`${API}/badge-count`);
    const count = data.count || 0;
    if ('setAppBadge' in navigator) {
      if (count > 0) await navigator.setAppBadge(count);
      else await navigator.clearAppBadge();
    }
    return count;
  } catch {
    return null;
  }
}
